import { useEffect, useRef } from 'react';

export interface VisibilityAwarePollingOptions {
    /** When false, no timer runs at all. */
    enabled: boolean;
    /**
     * When true, the callback also fires immediately each time the tab becomes visible
     * (in addition to the interval), so a backgrounded tab catches up the instant it's
     * refocused. When false, only the interval fires while visible.
     */
    runOnVisible?: boolean;
}

/**
 * Runs `callback` on an interval, but ONLY while the browser tab is visible. When the tab is
 * hidden the timer is cleared entirely (no polling in the background), and it resumes on
 * `visibilitychange` back to visible. This is the shared primitive for the app's presence
 * polling (case-editing status, homepage list refresh) -- deliberately visibility-gated so
 * backgrounded tabs never keep hammering the server (unlike the older notification-bell poll).
 *
 * The callback is held in a ref, so a changing callback identity does not tear down and rebuild
 * the timer -- only `intervalMs`, `enabled`, and `runOnVisible` do.
 */
export function useVisibilityAwarePolling(
    callback: () => void,
    intervalMs: number,
    opts: VisibilityAwarePollingOptions
): void {
    const { enabled, runOnVisible = false } = opts;
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    useEffect(() => {
        if (!enabled) return;

        let intervalId: ReturnType<typeof setInterval> | null = null;

        const startInterval = () => {
            if (intervalId !== null) return;
            intervalId = setInterval(() => callbackRef.current(), intervalMs);
        };

        const stopInterval = () => {
            if (intervalId !== null) {
                clearInterval(intervalId);
                intervalId = null;
            }
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                stopInterval();
            } else {
                if (runOnVisible) {
                    callbackRef.current();
                }
                startInterval();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Prime the timer for the current visibility state (don't fire immediately on mount --
        // callers that want an initial load do it themselves).
        if (!document.hidden) {
            startInterval();
        }

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            stopInterval();
        };
    }, [enabled, intervalMs, runOnVisible]);
}
